/** Billing page with balance summary, usage charts, and top consumers. */
import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  MenuItem,
} from "@mui/material";
import { Refresh, DateRange } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { addDays, subDays, format } from "date-fns";

import { billingApi } from "../api/client";
import BalanceCard from "../components/billing/BalanceCard";
import UsageChart from "../components/billing/UsageChart";
import TopConsumers from "../components/billing/TopConsumers";

interface BalanceSummary {
  total_credit: number;
  used_credit: number;
  available_credit: number;
  currency: string;
}

interface AccountBalance {
  account_id: string;
  name: string;
  balance: number;
  credit_limit: number;
  available: number;
  status: string;
}

interface BillingSummaryResponse {
  accounts: AccountBalance[];
  summary: BalanceSummary;
}

interface DailyUsage {
  date: string;
  total_calls: number;
  total_duration: number;
  total_cost: number;
}

interface TopConsumer {
  rank: number;
  account_id: string;
  name: string;
  total_calls: number;
  total_cost: number;
  avg_duration: number;
}

type DateRangePreset = "today" | "yesterday" | "last7days" | "last30days" | "custom";

/** Get date range based on preset. */
const getDateRange = (preset: DateRangePreset): { start: string; end: string } => {
  const today = new Date();
  const todayStr = format(today, "yyyy-MM-dd");

  switch (preset) {
    case "today":
      return { start: todayStr, end: todayStr };
    case "yesterday":
      return {
        start: format(subDays(today, 1), "yyyy-MM-dd"),
        end: format(subDays(today, 1), "yyyy-MM-dd"),
      };
    case "last7days":
      return {
        start: format(subDays(today, 6), "yyyy-MM-dd"),
        end: todayStr,
      };
    case "last30days":
      return {
        start: format(subDays(today, 29), "yyyy-MM-dd"),
        end: todayStr,
      };
    default:
      return { start: todayStr, end: todayStr };
  }
};

/**
 * Billing page component.
 *
 * Displays:
 * - Balance summary card (total/used/available credit)
 * - Daily usage chart (calls/duration/cost)
 * - Top consumers list ranked by cost
 * - Date range filter with presets
 * - Auto-refresh every 60 seconds
 */
const Billing: React.FC = () => {
  const navigate = useNavigate();

  // Data state
  const [balanceData, setBalanceData] = useState<BillingSummaryResponse | null>(null);
  const [usageData, setUsageData] = useState<DailyUsage[]>([]);
  const [topConsumers, setTopConsumers] = useState<TopConsumer[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [datePreset, setDatePreset] = useState<DateRangePreset>("last7days");

  // Fetch all billing data
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch balance summary
      const balanceResponse = await billingApi.summary();
      setBalanceData(balanceResponse.data);

      // Fetch usage data
      const dateRange = getDateRange(datePreset);
      const usageResponse = await billingApi.usage({
        start_date: dateRange.start,
        end_date: dateRange.end,
      });
      setUsageData(usageResponse.data);

      // Fetch top consumers
      const consumersResponse = await billingApi.topConsumers({
        start_date: dateRange.start,
        end_date: dateRange.end,
        limit: 10,
      });
      setTopConsumers(consumersResponse.data);
    } catch (error) {
      console.error("Failed to fetch billing data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, [datePreset]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [datePreset]);

  // Handle manual refresh
  const handleRefresh = () => {
    fetchData();
  };

  // Navigate to CDRs page
  const handleViewCDRs = () => {
    navigate("/cdrs");
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Billing
        </Typography>
        <Box display="flex" gap={2}>
          <TextField
            select
            label="Date Range"
            value={datePreset}
            onChange={(e) => setDatePreset(e.target.value as DateRangePreset)}
            size="small"
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="yesterday">Yesterday</MenuItem>
            <MenuItem value="last7days">Last 7 Days</MenuItem>
            <MenuItem value="last30days">Last 30 Days</MenuItem>
          </TextField>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Balance Summary Card */}
      {balanceData && <BalanceCard summary={balanceData.summary} />}

      {/* Usage Chart and Top Consumers */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={8}>
          <UsageChart data={usageData} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TopConsumers consumers={topConsumers} />
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              onClick={handleViewCDRs}
            >
              View Detailed CDRs
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Billing;
